import React, { useEffect, useMemo, useState, useCallback } from "react";
import { debounce } from "lodash";
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
  type MRT_ColumnFiltersState,
  type MRT_SortingState,
  MRT_PaginationState,
} from "material-react-table";
import { Button, Chip, MenuItem, Tooltip, IconButton } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import Title from "../../components/Title";
import { fetchCases, fetchCasesBySuiteId } from "../../features/slices/Case";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import { GlobalModal } from "../../components/GlobalModal";
import ManifestViewer from "../../components/ManifestViewer";
import ReportPreviewModal from "./components/ReportPreviewModal";
import TableActionButton from "../../components/table/TableActionButton";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { useParams, useNavigate } from "react-router-dom";
import Back from "../../components/Back";
import { useLoader } from "../../context/LoaderContext";
import {
  fetchAllureSingleReport,
  fetchCaseReport,
} from "../../features/slices/Report";
import { Case } from "../../interfaces/Case";
import { useNotification } from "../../context/NotificationContext";
import FormattedDateCell from "../../components/FormattedDateCell";

const getStatusColor = (status: string) => {
  switch (status) {
    case "PASSED":
      return "#4CAF50";

    case "FAILED":
      return "#FF9800";

    case "FAILED_ENSURE":
      return "#FF9800";

    case "CMD_ERROR":
    case "INTERNAL_ERROR":
      return "#E53935";

    case "NO_TESTS":
      return "#9E9E9E";

    case "RESERVED":
      return "gray";

    case "INIT":
      return "#9E9E9E";

    default:
      return "#9E9E9E";
  }
};

const getResultColor = (result: string) => {
  switch (result) {
    case "SUCCESS":
      return "success";
    case "FAILURE":
    case "ERROR":
      return "error";
    default:
      return "default";
  }
};

const CaseBySuite = () => {
  const [metaModal, setMetaModal] = useState(false);
  const { suitId } = useParams();
  const dispatch = useDispatch<AppDispatch>();
  const [data, setData] = useState<Case[]>([]);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [selectedMetadata, setSelectedMetadata] = useState<object>({});
  const [preview, setPreview] = useState<{
    open: boolean;
    caseId?: number;
    reportExists?: boolean;
  }>({ open: false });
  const [rowCount, setRowCount] = useState(0);
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([
    // {
    //   id: "status",
    //   value: statusType,
    // },
    // {
    //   id: "suite_id",
    //   value: suitId,
    // },
  ]);
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 100,
  });
  const navigate = useNavigate();
  const { showLoader, hideLoader } = useLoader();
  const { notify } = useNotification();
  const { user } = useSelector((state: RootState) => state.auth);

  const handleEdit = (id: number) => {
    navigate(`/cases/${id}/edit`);
  };
  const handleView = (id: number) => {
    navigate(`/cases/${id}/view`);
  };
  const handleAllure = async (suiteId: number, caseId: number) => {
    if (!user) {
      notify("User is not authenticated", "error");
      return;
    }

    try {
      await dispatch(
        fetchAllureSingleReport({
          userId: user?.id || 0,
          suiteId: suiteId,
          caseId: caseId,
        })
      ).unwrap();
    } catch (error) {
      notify("Error fetching report", "error");
      console.error("Error:", error);
    }
  };
  const handleDownload = async (caseId: number) => {
    try {
      const result = await dispatch(fetchCaseReport(Number(caseId))).unwrap();
      const url = window.URL.createObjectURL(
        new Blob([new Uint8Array(result.data)], {
          type: "application/zip",
        })
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `case_${caseId}.zip`);
      document.body.appendChild(link);
      link.click();
      notify("Downloaded report.", "success");
    } catch (error) {
      console.error("Error downloading report:", error);
      notify("Failed to download report.", "error");
    }
  };
  const handleMetaOpen = (metadata: object) => {
    setSelectedMetadata(metadata);
    setMetaModal(true);
  };
  const fetchData = useCallback(
    debounce(async () => {
      setIsLoading(true);
      setIsRefetching(true);
      const params = {
        page: pagination.pageIndex + 1,
        page_size: pagination.pageSize,
        sort_by: sorting[0]?.id || "id",
        order: sorting[0]?.desc ? "desc" : "asc",
        filter_by: JSON.stringify([{}]),
        // globalFilter: globalFilter || "",
      };

      const query = new URLSearchParams(params).toString();
      try {
        const data = await dispatch(fetchCasesBySuiteId(suitId)).unwrap();
        // setRowCount(data.total);
        setData(data.cases);
      } catch (error) {
        setIsError(true);
      } finally {
        setIsLoading(false);
        setIsRefetching(false);
      }
    }, 300),
    [dispatch] // temp suitId, pagination, globalFilter,
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const columns = useMemo<MRT_ColumnDef<Case>[]>(
    () => [
      {
        accessorKey: "id",
        header: "ID",
        enableColumnFilter: true,
        enableSorting: true,
      },
      {
        accessorKey: "allure",
        header: "Allure",
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <TableActionButton
            label="Report"
            icon={<AssessmentOutlinedIcon />}
            onClick={() =>
              handleAllure(Number(row.original.suite_id), Number(row.id))
            }
            disabled={!row.original.report_exists}
            tooltip={
              row.original.report_exists
                ? "Open Allure report"
                : "No report yet — run the case first"
            }
          />
        ),
      },
      {
        accessorKey: "suite_id",
        header: "Suite ID",
        enableColumnFilter: false,
        enableSorting: false,
      },

      {
        accessorKey: "title",
        header: "Title",
        filterVariant: "autocomplete",
        enableSorting: false,
        Cell: ({ cell }) => (
          <Tooltip title={cell.getValue<string>()}>
            <span>{cell.getValue<string>()}</span>
          </Tooltip>
        ),
      },
      {
        accessorKey: "executed_at",
        header: "Execute At",
        enableColumnFilter: false,
        enableSorting: true,
        Cell: ({ cell }) => <FormattedDateCell value={cell.getValue<Date>()} />,
      },
      {
        accessorKey: "tc_result",
        header: "TC Result",
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ cell }) => (
          <Chip
            sx={{
              minWidth: 96, height: 26, fontSize: "0.72rem",
              backgroundColor: getStatusColor(cell.getValue<string>()),
              color: "#FFFFFF",
            }}
            label={cell.getValue<string>()}
          />
        ),
      },
      {
        accessorKey: "sys_result",
        header: "Sys Result",
        filterVariant: "select",
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ cell }) => (
          <Chip
            sx={{ minWidth: 84, height: 26, fontSize: "0.72rem" }}
            color={getResultColor(cell.getValue<string>())}
            label={cell.getValue<string>()}
          />
        ),
      },
      {
        accessorKey: "file_name",
        header: "File Name",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        accessorKey: "manifest",
        header: "Manifest",
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ cell }) => (
          <TableActionButton
            label="View"
            variant="text"
            icon={<VisibilityOutlinedIcon />}
            onClick={() => handleMetaOpen(cell.getValue<any>())}
          />
        ),
      },
    ],
    []
  );

  const table = useMaterialReactTable({
    columns,
    data,
    mrtTheme: (theme) => ({
      baseBackgroundColor: theme.palette.background.default,
    }),
    muiTableContainerProps: {
      sx: { maxHeight: "calc(78vh - 112px)" },
    },
    muiPaginationProps: {
      color: "secondary",
      rowsPerPageOptions: [10, 25, 50, 100],
      shape: "rounded",
      variant: "outlined",
    },
    paginationDisplayMode: "pages",
    columnFilterDisplayMode: "popover",
    initialState: {
      pagination: { pageSize: 25, pageIndex: 0 },
      density: "compact",
      showGlobalFilter: false,
      columnPinning: {
        right: ["mrt-row-actions"],
      },
      columnVisibility: { sys_result: false },
    },
    manualFiltering: false,
    enableColumnResizing: true,
    // enableGlobalFilter: false,
    manualPagination: false, // TODO: set to True when be complete server-side pagination
    enableGrouping: false,
    enableRowSelection: false,
    enableRowActions: true,
    enableColumnOrdering: false,
    enableColumnPinning: true,
    enableStickyHeader: true,
    enableColumnFilterModes: true,
    positionActionsColumn: "last",
    enableFacetedValues: true,
    getRowId: (row) => String(row.id),
    layoutMode: "grid-no-grow",
    muiToolbarAlertBannerProps: isError
      ? {
          color: "error",
          children: "Error loading data",
        }
      : undefined,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,

    // rowCount,
    state: {
      pagination,
      columnFilters,
      globalFilter,
      isLoading,
      showAlertBanner: isError,
      showProgressBars: isRefetching,
      sorting,
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <Tooltip arrow title="Refresh Data">
        <IconButton onClick={() => fetchData()}>
          <RefreshIcon />
        </IconButton>
      </Tooltip>
    ),
    renderRowActionMenuItems: ({ closeMenu, row }) => [
      <MenuItem
        key={0}
        onClick={() => {
          handleEdit(Number(row.id));
          closeMenu();
        }}
        sx={{ m: 0 }}
      >
        Edit
      </MenuItem>,
      <MenuItem
        key={1}
        onClick={() => {
          handleView(Number(row.id));
          closeMenu();
        }}
        sx={{ m: 0 }}
      >
        View
      </MenuItem>,
      <MenuItem
        key={2}
        onClick={() => {
          handleDownload(Number(row.id));
          closeMenu();
        }}
        sx={{ m: 0 }}
      >
        Download
      </MenuItem>,
      <MenuItem
        key={3}
        disabled={!row.original.report_exists}
        onClick={() => {
          setPreview({
            open: true,
            caseId: Number(row.id),
            reportExists: !!row.original.report_exists,
          });
          closeMenu();
        }}
        sx={{ m: 0 }}
      >
        Preview report
      </MenuItem>,
    ],
  });

  return (
    <>
      <Title>{`Cases by - Suite ID: ${suitId}`}</Title>
      <MaterialReactTable table={table} />
      <GlobalModal
        open={metaModal}
        onClose={() => setMetaModal(false)}
        title="Resource manifest"
        height="auto"
      >
        <ManifestViewer data={selectedMetadata} />
      </GlobalModal>
      <ReportPreviewModal
        open={preview.open}
        caseId={preview.caseId}
        reportExists={preview.reportExists}
        onClose={() => setPreview({ open: false })}
      />
      <Back />
    </>
  );
};

export default CaseBySuite;
